
import React from 'react';
import { Progress } from '@/components/ui/progress';
import { BackendProgressStage } from './enrichmentTypes';
import { cn } from '@/lib/utils';

interface EnrichmentProgressBarProps {
  currentStage: BackendProgressStage | string;
  className?: string;
}

const getProgressValue = (stage: BackendProgressStage | string): number => {
  const progressMap: Record<string, number> = {
    'initial': 0,
    'queued': 10,
    'requested': 20,
    'initiated_lutra': 30,
    'pending_lutra_data': 40,
    'processing': 50,
    'extracting': 70,
    'finalizing': 85,
    'polling_for_results': 60,
    'processing_retrieved_data': 90,
    'completed': 100,
    'applied': 100,
    'error': 0,
    'error_initiating': 0,
    'error_polling': 0,
    'error_processing': 0,
    'error_lutra_failure': 0,
    'error_unknown': 0,
    'failed': 0, // Handle the new failed status
    'cancelled': 0,
    'unknown': 0,
    'none': 0
  };
  
  return progressMap[stage] || 0;
};

const getProgressColor = (stage: BackendProgressStage | string): string => {
  if (stage.startsWith('error_') || stage === 'error' || stage === 'failed' || stage === 'cancelled') {
    return 'bg-red-500';
  }
  if (stage === 'completed' || stage === 'applied') {
    return 'bg-green-500';
  }
  return 'bg-blue-500';
};

const getStageLabel = (stage: BackendProgressStage | string): string => {
  const labelMap: Record<string, string> = {
    'initial': 'Initializing',
    'queued': 'Queued',
    'requested': 'Requested',
    'initiated_lutra': 'Starting Enrichment',
    'pending_lutra_data': 'Preparing Data',
    'processing': 'Processing',
    'extracting': 'Extracting Data',
    'finalizing': 'Finalizing',
    'polling_for_results': 'Checking Results',
    'processing_retrieved_data': 'Processing Retrieved Data',
    'completed': 'Completed',
    'applied': 'Applied',
    'error': 'Error',
    'error_initiating': 'Error During Initiation',
    'error_polling': 'Error Checking Results',
    'error_processing': 'Error Processing',
    'error_lutra_failure': 'Provider Error',
    'error_unknown': 'Unknown Error',
    'failed': 'Failed', // Handle the new failed status
    'cancelled': 'Cancelled',
    'unknown': 'Unknown Status',
    'none': 'Not Started'
  };
  
  return labelMap[stage] || stage;
};

export const EnrichmentProgressBar: React.FC<EnrichmentProgressBarProps> = ({ 
  currentStage, 
  className 
}) => {
  const progressValue = getProgressValue(currentStage);
  const progressColor = getProgressColor(currentStage);
  const stageLabel = getStageLabel(currentStage);
  
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex justify-between items-center text-sm">
        <span className="text-muted-foreground">Progress</span>
        <span className="font-medium">{stageLabel}</span>
      </div>
      <Progress 
        value={progressValue} 
        className="h-2"
        indicatorClassName={progressColor}
      />
      <div className="text-xs text-muted-foreground text-right">
        {progressValue}%
      </div>
    </div>
  );
};
